import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Download, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What must a VAT invoice template include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A valid VAT invoice must include: supplier name, address, and VAT registration number; a unique sequential invoice number and invoice date; the customer name and address; a description of the goods or services; the net (pre-VAT) amount; the VAT rate applied; the VAT amount charged; and the total amount including VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I download a VAT invoice template as a PDF?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic lets you fill in a VAT invoice template online and download a professionally formatted PDF instantly, for free. No Word or Excel file to edit. Just enter your VAT number, rate, and line items and download.',
      },
    },
    {
      '@type': 'Question',
      name: 'What VAT rates does the template support?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The Invilic VAT invoice template supports any rate you enter. Common rates: UK standard VAT 20%, UK reduced rate 5%, EU standard rates 17% to 27% (varies by country), Australian GST 10%, and zero-rated (0%) for exports and exempt supplies.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is this VAT invoice template compliant with HMRC requirements?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Invilic includes all fields required by HMRC for a full VAT invoice: supplier name and VAT number, sequential invoice number, invoice date, customer details, description of supply, net amount, VAT rate, VAT amount, and gross total. Always verify with your accountant for your specific circumstances.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I use this template for reverse charge VAT?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. The Invilic VAT invoice template has a reverse charge option. When enabled, the VAT amount is shown as zero with a note indicating the customer is responsible for accounting for the VAT under the reverse charge mechanism.',
      },
    },
  ],
};

const regions = [
  { flag: '🇬🇧', name: 'United Kingdom', detail: 'Standard rate 20%, reduced rate 5%. HMRC requires a VAT number, sequential invoice number, and full tax breakdown.' },
  { flag: '🇪🇺', name: 'European Union', detail: 'Standard rates range from 17% (Luxembourg) to 27% (Hungary). VAT number format varies by member state.' },
  { flag: '🇦🇺', name: 'Australia (GST)', detail: 'GST rate 10%. ABN required on tax invoices. Registration threshold AUD $75,000 annual turnover.' },
];

export default function VatInvoiceTemplatePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="VAT Invoice Template: Free PDF Download for UK, EU & Australia | Invilic"
        description="Free VAT invoice template for UK, EU, and Australian businesses. Includes VAT number, net amount, VAT rate and total. Download professional PDF instantly. No sign-up."
        canonical="https://www.invilic.com/vat-invoice-template"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            UK · EU · Australia
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            VAT Invoice Template
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            A free VAT invoice template for UK, EU, and Australian businesses. Enter your VAT number and rate, and the VAT amount is calculated automatically. Download a compliant PDF in seconds.
          </p>
          <Link
            to="/create/vat"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-orange-500/20 transition-all duration-200 text-lg"
          >
            Use Free VAT Template <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">Free · No sign-up · PDF in seconds</p>
        </div>

        {/* Template fields */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's in the VAT Invoice Template
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Supplier name, address & VAT number',
              'Customer name and address',
              'Sequential invoice number and date',
              'Description of goods or services',
              'Net amount (excluding VAT)',
              'VAT rate (any rate you enter)',
              'VAT amount calculated automatically',
              'Gross total including VAT',
              'Payment due date and terms',
              'Reverse charge notation (optional)',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-orange-100 dark:border-orange-800/30 bg-orange-50 dark:bg-orange-900/10">
                <CheckCircle className="w-4 h-4 text-orange-500 dark:text-orange-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Regions */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">VAT by Region</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {regions.map(({ flag, name, detail }) => (
              <div key={name} className="p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="text-2xl mb-3">{flag}</div>
                <div className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-orange-500" />{name}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Steps */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Use the VAT Invoice Template
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { n: '1', title: 'Open the template', desc: 'Click "Use Free VAT Template" above. No download or account needed.' },
              { n: '2', title: 'Enter your VAT details', desc: 'Add your VAT number, the applicable VAT rate, and your line items.' },
              { n: '3', title: 'Download PDF', desc: 'VAT is calculated automatically. Download a compliant PDF instantly.' },
            ].map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-orange-500 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            VAT Invoice Template: FAQs
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Download Your VAT Invoice Template Now
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">HMRC-ready fields. Auto VAT calculation. Free PDF download.</p>
          <Link
            to="/create/vat"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-orange-500/20 transition-all duration-200 text-lg"
          >
            <Download className="w-5 h-5" /> Get Free VAT Invoice Template
          </Link>
        </section>
      </main>
    </div>
  );
}
